import { isHourlyValue } from "@/offering_v2/components/date-time-picker/date_time_picker";
import { useStripe } from "@stripe/react-stripe-js";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useToast } from "../components";
import { useGetPartnerOffering } from "../data/api";
import { buildPathWithParams, useQueryString } from "../hooks";
import { FirstDayOfWeek } from "../lib/datetime";
import {
  useTrackCheckoutStartedEcommerceEventOnMount,
  useTrackOrderCompletedEcommerceEvent,
} from "./analytics_ecommerce_events";
import {
  useLocationQuery,
  useQuoteQuery,
  useSpaceBookingPolicyQuery,
  useSpaceV2Query,
} from "./api";
import { MeetingRoomCheckoutBoxDesktop } from "./checkout_box_desktop";
import { pickerOverride } from "./checkout_box_mobile";
import { MeetingRoomDateRangePicker } from "./meeting_room_date_range_picker";
import {
  normalizeMeetingRoomOrderParams,
  toQuoteInput,
  useDateTimePickerHelper,
  useMeetingRoomOrderParamsHandlers,
} from "./order_params";
import { PartnerConfig } from "./partner_config";
import { Promotion } from "./template_offering_checkout_common";
import {
  TemplateOfferingCheckoutStripeBase,
  useCheckoutStripe,
} from "./template_offering_checkout_stripe_base";
import {
  getStartDateFromOrderParams,
  getEndDateFromOrderParams,
} from "./order_params_utils";

interface TemplateOfferingCheckoutStripeMeetingRoomParams {
  offeringID: string;
  spaceID: string;
}

interface TemplateOfferingCheckoutStripeMeetingRoomQueryParams {
  locationID?: string;
  startDate?: string;
  endDate?: string;
}

export interface TemplateOfferingCheckoutStripeMeetingRoomProps {
  showTalkWithUsPrompt?: boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  partnerConfig: PartnerConfig;
  defaultMarketingConsent?: boolean;
}

export function TemplateOfferingCheckoutStripeMeetingRoom(
  props: TemplateOfferingCheckoutStripeMeetingRoomProps
) {
  const {
    partnerConfig,
    firstDayOfWeek,
    showTalkWithUsPrompt,
    defaultMarketingConsent,
  } = props;
  const pageParams =
    useParams<TemplateOfferingCheckoutStripeMeetingRoomParams>();
  const partnerOfferingID = pageParams.offeringID;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID);
  const queryParams =
    useQueryString<TemplateOfferingCheckoutStripeMeetingRoomQueryParams>();
  const toast = useToast();
  const {
    orderParams,
    orderParamsError,
    validateOrderParams,
    handleChangeDateRange,
    handleChangePromoCode,
    handleChangeEmail,
  } = useMeetingRoomOrderParamsHandlers({
    ...normalizeMeetingRoomOrderParams(queryParams),
    spaceID: pageParams.spaceID,
  });
  const stripe = useStripe();
  const space = useSpaceV2Query(pageParams.spaceID);
  const selectedLocationQuery = useLocationQuery(space?.locationID);
  const { data: location } = selectedLocationQuery;
  const selectedSpaceID = space.id;
  const {
    isOutsideRange,
    isBlockedDate,
    handleNextVisibleDates,
    handleSelectDate,
    timeslots,
    availableDaysError,
    availableTimeSlotsError,
  } = useDateTimePickerHelper(selectedSpaceID, {
    dateRange: {
      startDate: isHourlyValue(orderParams.dateRange)
        ? orderParams.dateRange.date
        : orderParams.dateRange?.startDate,
      endDate: isHourlyValue(orderParams.dateRange)
        ? orderParams.dateRange.date
        : orderParams.dateRange?.startDate,
    },
  });
  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();
  useEffect(() => {
    if (space) setSpaceLayoutID(space.defaultLayoutID);
  }, [space, setSpaceLayoutID]);

  const quoteInput = useMemo(
    () =>
      selectedSpaceID && spaceLayoutID
        ? toQuoteInput(
            selectedSpaceID,
            partnerOffering.partner.id,
            orderParams,
            "WL",
            {
              layoutID: spaceLayoutID,
              attendees: 1,
            }
          )
        : null,
    [selectedSpaceID, partnerOffering.partner.id, orderParams, spaceLayoutID]
  );
  const {
    data: quote,
    error: quoteError,
    loading: quoteLoading,
  } = useQuoteQuery(quoteInput);
  useTrackCheckoutStartedEcommerceEventOnMount(
    space.name,
    quote,
    space,
    location,
    undefined,
    partnerOffering
  );
  const trackOrderCompletedEcommerceEvent =
    useTrackOrderCompletedEcommerceEvent(partnerOffering);

  const { data: spaceBookingPolicy } = useSpaceBookingPolicyQuery(
    space.id,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );
  const askForBookingReason = !!spaceBookingPolicy?.askForBookingReason;
  const quotePositivePrice = quote ? quote.totalPrice > 0 : false;
  const askForTax =
    (spaceBookingPolicy?.askForTax && quotePositivePrice) || false;

  const form = useCheckoutStripe({
    stripe,
    requireBillingDetails: quotePositivePrice,
    requirePayment: quotePositivePrice,
    askForTax,
    requireBookingReason: partnerConfig.requireBookingReason,
    initialValues: {
      fullName: "",
      companyName: "",
      phoneNumber: "",
      email: "",
      billingStreetAddress: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: "",
      billingCountry: "",
      billingTax: "",
      paymentComplete: false,
      termsChecked: false,
      marketingConsent: defaultMarketingConsent || false,
      bookingReason: "",
    },
    partnerID: partnerOffering.partner.id,
    partnerOfferingID: partnerOffering.id,
    legacy__details: {
      layoutID: spaceLayoutID || "",
      attendees: 1,
    },
    orderParams,
    onComplete: (order) => {
      trackOrderCompletedEcommerceEvent(space.name, order, space, location);

      const pathWithParams = buildPathWithParams(
        `/offerings/confirm_order/${partnerOffering.slug}/${order.id}`
      );
      history.push(pathWithParams);
    },
    onError: (error) => {
      toast.error({
        message: error,
      });
    },
  });

  const history = useHistory();
  const disabledCheckout = !!(
    (!form.values.paymentComplete && quotePositivePrice) ||
    quoteLoading ||
    quoteError ||
    orderParamsError.dateRange ||
    orderParamsError.spaceID
  );

  return (
    <TemplateOfferingCheckoutStripeBase
      disabledCheckout={disabledCheckout}
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      includeQuantity={false}
      offeringName={space.name}
      partnerOfferingSlug={partnerOfferingID}
      partnerConfig={partnerConfig}
      location={location}
      space={space}
      partnerOffering={partnerOffering}
      askForTax={askForTax}
      askForBookingReason={askForBookingReason}
      orderParams={orderParams}
      validateOrderParams={validateOrderParams}
      onChangeEmail={handleChangeEmail}
      promotion={
        <Promotion
          partnerID={partnerOffering.partner.id}
          promotableType="Space"
          promotableID={orderParams.spaceID}
          email={orderParams.email}
          onChangePromoCode={handleChangePromoCode}
          timeRange={
            isHourlyValue(orderParams.dateRange)
              ? orderParams.dateRange
              : undefined
          }
        />
      }
      quote={quote}
      onPressBack={() => {
        if (history.length > 1) {
          history.goBack();
        } else {
          const pathWithParams = buildPathWithParams(
            `/offerings/${partnerOfferingID}`
          );
          history.replace(pathWithParams);
        }
      }}
      mobileCheckoutBox={
        <MeetingRoomDateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="modal"
          ctaTitle="Save"
          timeslots={timeslots}
          value={
            isHourlyValue(orderParams.dateRange)
              ? orderParams.dateRange
              : undefined
          }
          onComplete={handleChangeDateRange}
          onNextVisibleDates={handleNextVisibleDates}
          onSelectDate={handleSelectDate}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          override={pickerOverride}
          placeholder={partnerConfig.datePickerPlaceholder}
          disabled={!!availableDaysError || !!availableTimeSlotsError}
        />
      }
      desktopCheckoutBox={
        <MeetingRoomCheckoutBoxDesktop
          quoteLoading={quoteLoading}
          firstDayOfWeek={firstDayOfWeek}
          orderParams={orderParams}
          orderParamsErrors={orderParamsError}
          timeslots={timeslots}
          isOutsideRange={isOutsideRange}
          isBlockedDate={isBlockedDate}
          onChangeDateRange={handleChangeDateRange}
          onNextVisibleDates={handleNextVisibleDates}
          location={location}
          space={space}
          onSelectDate={handleSelectDate}
          quote={quote}
          showCTA={false}
          datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
          availableDaysError={availableDaysError}
          availableTimeSlotsError={availableTimeSlotsError}
        />
      }
      form={form}
    />
  );
}
